import { html, css, LitElement, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';

import '../typography/fhi-body/fhi-body.component';

export const FhiCheckboxSelector = 'fhi-checkbox';

/**
 * ## FHI Checkbox
 *
 * {@link https://designsystem.fhi.no/?path=/docs/komponenter-checkbox--docs}
 *
 * The `<fhi-checkbox>` component represents a checkbox input element styled and implemented according to the FHI Design System guidelines.
 * It allows users to make binary choices, such as selecting or deselecting an option.
 *
 * @tag fhi-checkbox
 * @element fhi-checkbox
 */
@customElement(FhiCheckboxSelector)
export class FhiCheckbox extends LitElement {
  /** @internal */
  static readonly formAssociated = true;

  /**
   * The text label assigned to and displayed next to the checkbox.
   * You should always provide a label.
   * @type {string}
   */
  @property({ type: String }) label?: string = undefined;

  /**
   * The name of the checkbox. This is submitted with the form data as a `key` when the checkbox is checked.
   * @type {string}
   */
  @property({ type: String }) name?: string = undefined;

  /**
   * The value of the checkbox. This is submitted with the form data as a `value` when the checkbox is checked.
   * @type {string}
   */
  @property({ type: String }) value: string = 'on';

  /**
   * The help text shown below the label.
   * This is used to provide additional information or descriptions to the user.
   * @type {string}
   */
  @property({ type: String, attribute: 'help-text' }) helpText?: string;

  /**
   * Sets the visual status of the checkbox. There is currently only one status available: `error`.
   * The `error` status is used to indicate that there is an issue with the checkbox, such as a required checkbox not being checked.
   * @reflect
   * @type {'error' | undefined}
   */
  @property({ type: String, reflect: true }) status?: 'error' | undefined;

  /**
   * Whether the checkbox is checked or not.
   * @type {boolean}
   */
  @property({ type: Boolean }) checked? = false;

  /**
   * Disables the checkbox. This changes its appearance and makes it non-interactive.
   * @reflect
   * @type {boolean}
   */
  @property({ type: Boolean, reflect: true }) disabled? = false;

  private _internals: ElementInternals;

  constructor() {
    super();
    this._internals = this.attachInternals();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._updateFormValue();
  }

  protected willUpdate(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('value')) {
      this.value = this.value ?? 'on';
    }
  }

  protected updated(_changedProperties: PropertyValues<FhiCheckbox>): void {
    if (_changedProperties.has('helpText') || _changedProperties.has('label')) {
      if (!this.label && this.helpText) {
        console.error(
          'The "help-text" property is set, but the "label" property is not. It is required to provide a label when using help text.',
        );
      }
    }
  }

  private _handleChange(event: Event): void {
    this.checked = (event.target as HTMLInputElement).checked;
    this._updateFormValue();

    event.stopPropagation();
    this._dispatchChangeEvent();
  }

  private _handleInput(event: Event): void {
    event.stopPropagation();
    this._dispatchInputEvent();
  }

  private _updateFormValue() {
    this._internals.setFormValue(this.checked ? this.value : null);
  }

  private _dispatchChangeEvent(): void {
    /**
     * @type {Event} - Standard DOM event with the type `change`.
     * This event is dispatched when the checkbox is checked or unchecked.
     */
    this.dispatchEvent(new Event('change', { bubbles: true }));
  }

  private _dispatchInputEvent(): void {
    /**
     * @type {Event} - Standard DOM event with the type `input`.
     * This event is dispatched when the checkbox is checked or unchecked.
     */
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  }

  public formResetCallback() {
    this.checked = typeof this.getAttribute('checked') === 'string';
    this._updateFormValue();
  }

  render() {
    return html`
      <div class="checkbox-wrapper">
        <input
          type="checkbox"
          id="checkbox-element"
          name="${ifDefined(this.name)}"
          value="${ifDefined(this.value)}"
          ?disabled=${this.disabled}
          aria-invalid=${ifDefined(
            this.status === 'error' ? 'true' : undefined,
          )}
          .checked=${live(this.checked ?? false)}
          @change=${this._handleChange}
          @input=${this._handleInput}
        />
        <svg
          class="checkmark"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.043 6.04295C12.4335 5.65243 13.0666 5.65243 13.4571 6.04295C13.8476 6.43348 13.8476 7.06649 13.4571 7.45702L8.95708 11.957C8.56655 12.3475 7.93354 12.3475 7.54302 11.957L5.29302 9.70702C4.90249 9.31649 4.90249 8.68348 5.29302 8.29295C5.65913 7.92684 6.23813 7.90424 6.63091 8.22459L6.70708 8.29295L8.25005 9.83592L12.043 6.04295Z"
          />
        </svg>
      </div>
      ${this.label && html`<label for="checkbox-element">${this.label}</label>`}
      ${this.helpText
        ? html`<fhi-body size="small" class="help-text"
            >${this.helpText}</fhi-body
          >`
        : ''}
    `;
  }

  static styles = css`
    :host {
      --fhi-checkbox-color: currentcolor;
    }

    :host {
      display: grid;
      width: max-content;
      height: fit-content;
      color: var(--fhi-checkbox-color);

      .checkbox-wrapper {
        position: relative;
        height: fit-content;
      }

      label {
        font-family: var(--fhi-font-family-default);
        -webkit-font-smoothing: antialiased;
        font-size: var(--fhi-typography-body-medium-font-size);
        font-weight: var(--fhi-typography-body-medium-font-weight);
        line-height: var(--fhi-typography-body-medium-line-height);
        letter-spacing: var(--fhi-typography-body-medium-letter-spacing);
      }

      label,
      fhi-body {
        grid-column: 2;
        padding-left: 7px;
      }

      input[type='checkbox'] {
        margin: 3px 0 0 3px;
        appearance: none;
        width: 1.125rem;
        height: 1.125rem;
        background-color: var(--fhi-color-neutral-background-default);
        border: var(--fhi-dimension-border-width) solid
          var(--fhi-color-neutral-border-default);
        border-radius: var(--fhi-border-radius-050);
        transition: all var(--fhi-motion-ease-default)
          var(--fhi-motion-duration-quick);
        &:not(:focus-visible) {
          outline-color: var(--fhi-color-accent-surface-hover);
        }

        &:hover {
          background-color: var(--fhi-color-accent-background-subtle);
          border-color: var(--fhi-color-accent-border-strong);
        }

        &:active {
          background-color: var(--fhi-color-accent-surface-default);
          outline: solid 5px var(--fhi-color-accent-surface-hover);
          border-color: var(--fhi-color-accent-border-strong);
        }

        &:checked {
          background-color: var(--fhi-color-neutral-base-default);
          border-color: var(--fhi-color-neutral-base-default);
          ~ .checkmark {
            fill: var(--fhi-color-neutral-text-inverted);
            visibility: visible;
            opacity: 1;
          }

          &:hover {
            background-color: var(--fhi-color-accent-base-hover);
            border-color: var(--fhi-color-accent-base-hover);
          }
          &:active {
            background-color: var(--fhi-color-accent-base-active);
            border-color: var(--fhi-color-accent-base-active);
          }
        }
      }

      .checkmark {
        visibility: hidden;
        opacity: 0;
        transition: opacity var(--fhi-motion-ease-default)
          var(--fhi-motion-duration-quick);
        position: absolute;
        top: 3px;
        left: 3px;
        height: 1.125rem;
        width: 1.125rem;
        pointer-events: none;
      }

      .help-text {
        color: var(--fhi-color-neutral-text-subtle);
      }
    }

    :host([disabled]) {
      opacity: var(--fhi-opacity-disabled);
      * {
        cursor: not-allowed;
      }

      input {
        &:hover {
          background-color: var(--fhi-color-neutral-background-default);
          border-color: var(--fhi-color-neutral-border-default);
        }

        &:active {
          outline: none;
          background-color: var(--fhi-color-neutral-background-default);
          border-color: var(--fhi-color-neutral-border-default);
        }

        &:checked:hover {
          background-color: var(--fhi-color-neutral-base-default);
          border-color: var(--fhi-color-neutral-base-default);
        }

        &:checked:active {
          background-color: var(--fhi-color-neutral-base-default);
          border-color: var(--fhi-color-neutral-base-default);
        }
      }
    }

    :host([status='error']:not([disabled])) {
      input[type='checkbox'] {
        background-color: var(--fhi-color-danger-background-default);
        border-color: var(--fhi-color-danger-border-default);
        &:not(:focus-visible) {
          outline-color: var(--fhi-color-danger-surface-hover);
        }

        &:hover {
          background-color: var(--fhi-color-danger-background-subtle);
          border-color: var(--fhi-color-danger-border-strong);
        }

        &:active {
          background-color: var(--fhi-color-danger-surface-default);
          border-color: var(--fhi-color-danger-border-strong);
          outline-color: var(--fhi-color-danger-surface-hover);
        }

        &:checked {
          background-color: var(--fhi-color-danger-base-default);
          border-color: var(--fhi-color-danger-base-default);
          &:hover {
            background-color: var(--fhi-color-danger-base-hover);
            border-color: var(--fhi-color-danger-base-hover);
          }

          &:active {
            background-color: var(--fhi-color-danger-base-active);
            border-color: var(--fhi-color-danger-base-active);
          }
        }
      }
    }
  `;
}
